import React, { useState, useEffect } from "react";
import { useToast } from "@/shared/components/ui/use-toast";
import { Toaster } from "@/shared/components/ui/toaster";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Switch } from "@/shared/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Badge } from "@/shared/components/ui/badge";
import { Copy, RefreshCw, X, Plus } from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import * as adminService from "@/shared/api/services/adminService";

function generateApiKey() {
  return "pk_live_" + Array.from({ length: 32 }, () =>
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[Math.floor(Math.random() * 62)]
  ).join("");
}

function generateSecretKey() {
  return "sk_live_" + Array.from({ length: 40 }, () =>
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[Math.floor(Math.random() * 62)]
  ).join("");
}

const DARK_STYLES = `
  .mf-dialog [data-radix-scroll-area-viewport] { background: #0a1628; }

  .mf-tabs [role="tablist"] {
    background-color: #0d1f3c; border: 1px solid #1e3a5f;
    border-radius: 8px; padding: 4px;
  }
  .mf-tabs [role="tab"] { color: #94a3b8; border-radius: 6px; transition: all 0.2s; font-size: 0.8rem; }
  .mf-tabs [role="tab"]:hover { color: #67e8f9; background-color: #0f2d4a; }
  .mf-tabs [role="tab"][data-state="active"] {
    background-color: #0e4d6e; color: #67e8f9;
    box-shadow: 0 0 12px rgba(103,232,249,0.15);
  }

  /* Inputs & Textarea — dark navy + full autofill fix */
  .mf-dialog input:not([type="checkbox"]),
  .mf-dialog textarea {
    background-color: #0d1f3c !important;
    border: 1px solid #1e3a5f !important;
    color: #e2e8f0 !important;
    border-radius: 6px;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .mf-dialog input:not([type="checkbox"]):-webkit-autofill,
  .mf-dialog input:not([type="checkbox"]):-webkit-autofill:hover,
  .mf-dialog input:not([type="checkbox"]):-webkit-autofill:focus,
  .mf-dialog input:not([type="checkbox"]):-internal-autofill-selected {
    -webkit-box-shadow: 0 0 0px 1000px #0d1f3c inset !important;
    -webkit-text-fill-color: #e2e8f0 !important;
    caret-color: #e2e8f0 !important;
  }
  .mf-dialog input:not([type="checkbox"]):hover,
  .mf-dialog textarea:hover { border-color: #22d3ee !important; }
  .mf-dialog input:not([type="checkbox"]):focus,
  .mf-dialog textarea:focus {
    border-color: #06b6d4 !important;
    box-shadow: 0 0 0 2px rgba(6,182,212,0.2) !important;
    outline: none !important;
  }
  .mf-dialog input::placeholder, .mf-dialog textarea::placeholder { color: #475569 !important; }
  .mf-dialog input:disabled { opacity: 0.5; cursor: not-allowed; }

  .mf-dialog [role="combobox"] {
    background-color: #0d1f3c !important;
    border: 1px solid #1e3a5f !important;
    color: #e2e8f0 !important;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .mf-dialog [role="combobox"]:hover { border-color: #22d3ee !important; }
  .mf-dialog [role="combobox"]:focus {
    border-color: #06b6d4 !important;
    box-shadow: 0 0 0 2px rgba(6,182,212,0.2) !important;
  }

  [data-radix-popper-content-wrapper] [role="listbox"] {
    background-color: #0d1f3c !important; border: 1px solid #1e3a5f !important; color: #e2e8f0 !important;
  }
  [data-radix-popper-content-wrapper] [role="option"] { color: #e2e8f0 !important; }
  [data-radix-popper-content-wrapper] [role="option"]:hover,
  [data-radix-popper-content-wrapper] [role="option"]:focus,
  [data-radix-popper-content-wrapper] [role="option"][data-highlighted] {
    background-color: #0e4d6e !important; color: #67e8f9 !important;
  }

  .mf-dialog label {
    color: #94a3b8; font-size: 0.75rem; font-weight: 500;
    letter-spacing: 0.04em; text-transform: uppercase;
  }

  .mf-dialog [role="switch"] { background-color: #1e3a5f !important; }
  .mf-dialog [role="switch"][data-state="checked"] { background-color: #06b6d4 !important; }

  .mf-ip-badge {
    background-color: #0e4d6e !important;
    border: 1px solid #0e7490 !important;
    color: #67e8f9 !important;
  }

  .mf-warn { background-color: #1c1400; border: 1px solid #854d0e; border-radius: 8px; color: #fde68a; }

  .mf-section-bg { background-color: #0d1f3c; border: 1px solid #1e3a5f; border-radius: 8px; }

  .mf-dialog input.font-mono { letter-spacing: 0.02em; }
  .mf-hint { color: #475569; font-size: 0.72rem; }

  .mf-dialog input.error-border,
  .mf-dialog button.error-border {
    border-color: #ef4444 !important;
  }
  .mf-dialog input.error-border:focus,
  .mf-dialog button.error-border:focus {
    box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2) !important;
  }
`;

export default function MerchantForm({ merchant, open, onClose, onSave, engines = [], resellers = [], apiError, onClearError }) {
  const { toast } = useToast();
  const { t } = useLanguage();

  useEffect(() => {
    if (apiError) {
      toast({
        title: "Error",
        description: apiError,
        variant: "destructive",
      });
      if (onClearError) onClearError();
    }
  }, [apiError, toast, onClearError]);

  const [currentTab, setCurrentTab] = useState("general");
  const [formData, setFormData] = useState(
    merchant
      ? {
        ...merchant,
        api_key: merchant.apiKey,
        secret_key: merchant.secretKey,
        callback_url: merchant.callbackUrl,
        webhook_url: merchant.webhookUrl,
      }
      : {
        name: "",
        code: "",
        email: "",
        password: "",
        status: "ACTIVE",
        parent_type: "direct",
        engine_id: "",
        reseller_id: "",
        deposit_enabled: true,
        withdrawal_enabled: true,
        notes: "",
        api_key: generateApiKey(),
        secret_key: generateSecretKey(),
        callback_url: "",
        webhook_url: "",
        ip_whitelist: [],
        rate_limit: 100,
        min_deposit: 100,
        max_deposit: 1000000,
        min_withdrawal: 100,
        max_withdrawal: 500000,
        daily_deposit_limit: 10000000,
        daily_withdrawal_limit: 5000000,
        deposit_fee: null,
        withdrawal_fee: null,
        settlement_period: "T1",
        contact_name: "",
        contact_email: "",
        contact_phone: "",
      }
  );
  const [newIp, setNewIp] = useState("");
  const [saving, setSaving] = useState(false);
  const [keysRegenerated, setKeysRegenerated] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setCurrentTab("general");
      setErrors({});
      if (merchant) {
        setFormData((prev) => ({
          ...prev,
          ...merchant,
          api_key: merchant.apiKey ?? "",
          secret_key: merchant.secretKey ? "••••••••" : "",
          callback_url: merchant.callbackUrl ?? prev.callback_url ?? "",
          webhook_url: merchant.webhookUrl ?? prev.webhook_url ?? "",
          password: "",
          parent_type: merchant.createdByEngineId ? "engine" : merchant.createdByResellerId ? "reseller" : "direct",
          engine_id: merchant.createdByEngineId ?? "",
          reseller_id: merchant.createdByResellerId ?? "",
          deposit_enabled: merchant.depositEnabled ?? true,
          withdrawal_enabled: merchant.withdrawalEnabled ?? true,
          notes: merchant.notes ?? "",
          ip_whitelist: merchant.ipWhitelist ?? [],
          rate_limit: merchant.rateLimit ?? 100,
          min_deposit: merchant.minDeposit ?? 100,
          max_deposit: merchant.maxDeposit ?? 1000000,
          min_withdrawal: merchant.minWithdrawal ?? 100,
          max_withdrawal: merchant.maxWithdrawal ?? 500000,
          daily_deposit_limit: merchant.dailyDepositLimit ?? 10000000,
          daily_withdrawal_limit: merchant.dailyWithdrawalLimit ?? 5000000,
          deposit_fee: merchant.depositFee ?? null,
          withdrawal_fee: merchant.withdrawalFee ?? null,
          settlement_period: merchant.settlementPeriod ?? "T1",
          contact_name: merchant.contactName ?? "",
          contact_email: merchant.contactEmail ?? "",
          contact_phone: merchant.contactPhone ?? "",
        }));
        setKeysRegenerated(false);
      } else {
        setFormData((prev) => ({
          ...prev,
          api_key: generateApiKey(),
          secret_key: generateSecretKey(),
        }));
      }
    }
  }, [open, merchant]);

  const handleChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const addIp = () => {
    if (newIp && !formData.ip_whitelist?.includes(newIp)) {
      handleChange("ip_whitelist", [...(formData.ip_whitelist || []), newIp]);
      setNewIp("");
    }
  };

  const removeIp = (ip) => handleChange("ip_whitelist", formData.ip_whitelist?.filter((i) => i !== ip));

  const regenerateKeys = () => {
    setKeysRegenerated(true);
    handleChange("api_key", generateApiKey());
    handleChange("secret_key", generateSecretKey());
  };

  const copyToClipboard = (text) => navigator.clipboard.writeText(text);

  const handleSubmit = async () => {

    const hasGeneralErrors = generalTabInputHandler();
    if (hasGeneralErrors) return;

    const hasApiErrors = apiTabInputHandler();
    if (hasApiErrors) return;

    setSaving(true);
    try {
      let dataToSave;
      if (merchant) {
        dataToSave = {
          name: formData.name,
          status: formData.status,
          webhookUrl: formData.webhook_url,
          callbackUrl: formData.callback_url,
          depositEnabled: formData.deposit_enabled,
          withdrawalEnabled: formData.withdrawal_enabled,
          notes: formData.notes,
          ipWhitelist: formData.ip_whitelist,
          rateLimit: Number(formData.rate_limit) || null,
          minDeposit: Number(formData.min_deposit) || null,
          maxDeposit: Number(formData.max_deposit) || null,
          minWithdrawal: Number(formData.min_withdrawal) || null,
          maxWithdrawal: Number(formData.max_withdrawal) || null,
          dailyDepositLimit: Number(formData.daily_deposit_limit) || null,
          dailyWithdrawalLimit: Number(formData.daily_withdrawal_limit) || null,
          depositFee: formData.deposit_fee !== null ? Number(formData.deposit_fee) : null,
          withdrawalFee: formData.withdrawal_fee !== null ? Number(formData.withdrawal_fee) : null,
          settlementPeriod: formData.settlement_period,
          contactName: formData.contact_name,
          contactEmail: formData.contact_email,
          contactPhone: formData.contact_phone,
          parentType: formData.parent_type,
          engineId: formData.engine_id,
          resellerId: formData.reseller_id,
        };
        if (keysRegenerated) {
          dataToSave.apiKey = formData.api_key;
          dataToSave.secretKey = formData.secret_key;
        }
        if (formData.password && formData.password.trim() !== "") {
          try {
            await adminService.changeMerchantPassword(merchant.id, formData.password);
          } catch (error) {
            console.error("Failed to change password:", error);
            toast({
              title: "Error",
              description: error?.response?.data?.message || error?.message || "Failed to change password.",
              variant: "destructive",
            });
            setSaving(false);
            return;
          }
        }
      } else {
        dataToSave = {
          name: formData.name,
          code: formData.code,
          email: formData.email,
          password: formData.password,
          apiKey: formData.api_key,
          secretKey: formData.secret_key,
          webhookUrl: formData.webhook_url || undefined,
          callbackUrl: formData.callback_url || undefined,
          depositEnabled: formData.deposit_enabled,
          withdrawalEnabled: formData.withdrawal_enabled,
          notes: formData.notes,
          ipWhitelist: formData.ip_whitelist || [],
          rateLimit: Number(formData.rate_limit) || null,
          minDeposit: Number(formData.min_deposit) || null,
          maxDeposit: Number(formData.max_deposit) || null,
          minWithdrawal: Number(formData.min_withdrawal) || null,
          maxWithdrawal: Number(formData.max_withdrawal) || null,
          dailyDepositLimit: Number(formData.daily_deposit_limit) || null,
          dailyWithdrawalLimit: Number(formData.daily_withdrawal_limit) || null,
          depositFee: Number(formData.deposit_fee) || null,
          withdrawalFee: Number(formData.withdrawal_fee) || null,
          settlementPeriod: formData.settlement_period,
          contactName: formData.contact_name,
          contactEmail: formData.contact_email,
          contactPhone: formData.contact_phone,
          parentType: formData.parent_type,
          engineId: formData.parent_type === "engine" ? formData.engine_id : undefined,
          resellerId: formData.parent_type === "reseller" ? formData.reseller_id : undefined,
        };
      }
      await onSave(dataToSave);
    } catch (error) {
      console.error("Failed to save merchant:", error);
    } finally {
      setSaving(false);
    }
  };

  const tabOrder = ["general", "api", "limits", "contact"];
  const currentTabIndex = tabOrder.indexOf(currentTab);
  const isLastTab = currentTabIndex === tabOrder.length - 1;
  const isFirstTab = currentTabIndex === 0;

  const tabChangeHandler = () => {
    if (isLastTab) return;

    if (currentTab === "general") {
      const hasErrors = generalTabInputHandler();
      if (hasErrors) return;
    }

    if (currentTab === 'api') {
      const hasErrors = apiTabInputHandler();
      if (hasErrors) return;
    }

    setCurrentTab(tabOrder[currentTabIndex + 1])
  };

  const apiTabInputHandler = () => {
    const newErrors = {};
    if (!formData.api_key?.trim()) newErrors.api_key = true;
    if (!formData.secret_key?.trim()) newErrors.secret_key = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setCurrentTab("api");
      toast({
        title: "Validation Error",
        description: t("pleaseFillRequiredFields") || "Please fill all required fields",
        variant: "destructive",
      });
      return true;
    }
    return false;
  };

  const generalTabInputHandler = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = true;
    if (!formData.code?.trim()) newErrors.code = true;
    if (!formData.email?.trim()) newErrors.email = true;
    if (!merchant && !formData.password?.trim()) newErrors.password = true;
    if (formData.parent_type === "engine" && !formData.engine_id) newErrors.engine_id = true;
    if (formData.parent_type === "reseller" && !formData.reseller_id) newErrors.reseller_id = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setCurrentTab("general");
      toast({
        title: "Validation Error",
        description: t("pleaseFillRequiredFields") || "Please fill all required fields",
        variant: "destructive",
      });
      return true;
    }
    return false;
  };

  const btnPrimary = { background: 'linear-gradient(135deg,#0e7490,#0891b2)', color: '#fff', border: 'none' };
  const btnOutline = { background: 'transparent', border: '1px solid #1e3a5f', color: '#94a3b8' };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <style>{DARK_STYLES}</style>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto border border-[#1e3a5f] shadow-2xl shadow-cyan-900/20" style={{ backgroundColor: '#0a1628', color: '#e2e8f0' }}>
        <DialogHeader>
          <DialogTitle style={{ color: '#67e8f9' }} className="text-lg font-semibold tracking-wide">
            {merchant ? t("editMerchant") : t("addNewMerchant")}
          </DialogTitle>
        </DialogHeader>

        <div className="mf-dialog mf-tabs">
          <Tabs value={currentTab} onValueChange={setCurrentTab} className="mt-4">
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="general">{t("general")}</TabsTrigger>
              <TabsTrigger value="api">{t("apiSettings")}</TabsTrigger>
              <TabsTrigger value="limits">{t("limitsFees")}</TabsTrigger>
              <TabsTrigger value="contact">{t("contact")}</TabsTrigger>
            </TabsList>

            {/* GENERAL */}
            <TabsContent value="general" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={errors.name ? "text-red-500" : ""}>{t("merchantName")} *</Label>
                  <Input
                    value={formData.name || ""}
                    onChange={(e) => {
                      handleChange("name", e.target.value);
                      if (errors.name) setErrors(prev => ({ ...prev, name: false }));
                    }}
                    placeholder={t("merchantNamePlaceholder")}
                    className={errors.name ? "error-border" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label className={errors.code ? "text-red-500" : ""}>{t("merchantCode")} *</Label>
                  <Input
                    value={formData.code || ""}
                    onChange={(e) => {
                      handleChange("code", e.target.value.toUpperCase());
                      if (errors.code) setErrors(prev => ({ ...prev, code: false }));
                    }}
                    placeholder={t("merchantCodePlaceholder")}
                    className={errors.code ? "error-border" : ""}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className={errors.email ? "text-red-500" : ""}>{t("emailLabel")} *</Label>
                <Input
                  type="email"
                  value={formData.email || ""}
                  onChange={(e) => {
                    handleChange("email", e.target.value);
                    if (errors.email) setErrors(prev => ({ ...prev, email: false }));
                  }}
                  placeholder={t("emailPlaceholder")}
                  disabled={!!merchant}
                  required
                  className={errors.email ? "error-border" : ""}
                />
                {merchant && <p className="mf-hint">{t("emailCannotBeChanged")}</p>}
              </div>

              {!merchant ? (
                <div className="space-y-2">
                  <Label className={errors.password ? "text-red-500" : ""}>{t("password")} *</Label>
                  <Input
                    type="password"
                    value={formData.password || ""}
                    onChange={(e) => {
                      handleChange("password", e.target.value);
                      if (errors.password) setErrors(prev => ({ ...prev, password: false }));
                    }}
                    placeholder={t("passwordPlaceholder")}
                    required
                    className={errors.password ? "error-border" : ""}
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>{t("changePassword")}</Label>
                  <Input type="password" value={formData.password || ""} onChange={(e) => handleChange("password", e.target.value)} placeholder={t("leaveBlankToKeepPassword")} />
                  <p className="mf-hint">{t("changePasswordDescription")}</p>
                </div>
              )}

              {merchant && (
                <div className="space-y-2">
                  <Label>{t("status")}</Label>
                  <Select value={formData.status} onValueChange={(v) => handleChange("status", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVE">{t("active")}</SelectItem>
                      <SelectItem value="INACTIVE">{t("inactive")}</SelectItem>
                      <SelectItem value="SUSPENDED">{t("suspended")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>{t("parentType")}</Label>
                <Select disabled={!!merchant} value={formData.parent_type || "direct"} onValueChange={(v) => { handleChange("parent_type", v); handleChange("engine_id", ""); handleChange("reseller_id", ""); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direct">{t("directAdmin")}</SelectItem>
                    <SelectItem value="engine">{t("engine")}</SelectItem>
                    <SelectItem value="reseller">{t("reseller")}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="mf-hint">{t("whoManagesMerchant")}</p>
              </div>

              {formData.parent_type === "engine" && (
                <div className="space-y-2">
                  <Label className={errors.engine_id ? "text-red-500" : ""}>{t("engine")} *</Label>
                  <Select disabled={!!merchant} value={formData.engine_id?.toString() || ""} onValueChange={(v) => {
                    handleChange("engine_id", v);
                    if (errors.engine_id) setErrors(prev => ({ ...prev, engine_id: false }));
                  }}>
                    <SelectTrigger className={errors.engine_id ? "error-border" : ""}><SelectValue placeholder={t("selectEngine")} /></SelectTrigger>
                    <SelectContent>
                      {engines.map((e) => (
                        <SelectItem key={e.id} value={e.id.toString()}>{e.name} {e.email && `(${e.email})`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.parent_type === "reseller" && (
                <div className="space-y-2">
                  <Label className={errors.reseller_id ? "text-red-500" : ""}>{t("reseller")} *</Label>
                  <Select disabled={!!merchant} value={formData.reseller_id?.toString() || ""} onValueChange={(v) => {
                    handleChange("reseller_id", v);
                    if (errors.reseller_id) setErrors(prev => ({ ...prev, reseller_id: false }));
                  }}>
                    <SelectTrigger className={errors.reseller_id ? "error-border" : ""}><SelectValue placeholder={t("selectReseller")} /></SelectTrigger>
                    <SelectContent>
                      {resellers.map((r) => (
                        <SelectItem key={r.id} value={r.id.toString()}>{r.name} {r.email && `(${r.email})`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {[
                { key: "deposit_enabled", label: t("enableDeposits"), desc: t("allowDepositTransactions") },
                { key: "withdrawal_enabled", label: t("enableWithdrawals"), desc: t("allowWithdrawalTransactions") },
              ].map(({ key, label, desc }) => (
                <div key={key} className="mf-section-bg flex items-center justify-between p-4">
                  <div>
                    <Label style={{ textTransform: 'none', fontSize: '0.85rem', letterSpacing: 'normal', color: '#cbd5e1' }}>{label}</Label>
                    <p className="mf-hint mt-0.5">{desc}</p>
                  </div>
                  <Switch checked={formData[key]} onCheckedChange={(v) => handleChange(key, v)} />
                </div>
              ))}

              <div className="space-y-2">
                <Label>{t("notes")}</Label>
                <Textarea value={formData.notes} onChange={(e) => handleChange("notes", e.target.value)} placeholder={t("internalNotes")} rows={3} />
              </div>
            </TabsContent>

            {/* API */}
            <TabsContent value="api" className="space-y-4 mt-4">
              <div className="mf-warn p-3 text-sm">
                <strong style={{ color: '#fbbf24' }}>{t("security")}:</strong>{" "}
                <span style={{ color: '#fde68a' }}>{t("apiKeysShownOnce")}</span>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className={errors.api_key ? "text-red-500" : ""}>{t("apiKeyPublic")}</Label>
                  <button onClick={regenerateKeys} className="flex items-center gap-1 text-xs px-2 py-1 rounded transition-all"
                    style={{ color: '#94a3b8', background: 'transparent', border: '1px solid #1e3a5f' }}
                    onMouseEnter={e => { e.currentTarget.style.color = '#67e8f9'; e.currentTarget.style.borderColor = '#06b6d4'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.borderColor = '#1e3a5f'; }}>
                    <RefreshCw className="h-3 w-3" /> {t("regenerate")}
                  </button>
                </div>
                <div className="flex gap-2">
                  <Input value={formData.api_key} readOnly className={`font-mono text-sm ${errors.api_key ? "error-border" : ""}`} placeholder={t("apisettingsapiKeyPlaceholder")} />
                  <button onClick={() => copyToClipboard(formData.api_key)} className="px-3 rounded-md border transition-all"
                    style={{ background: 'transparent', border: '1px solid #1e3a5f', color: '#94a3b8' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#06b6d4'; e.currentTarget.style.color = '#67e8f9'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e3a5f'; e.currentTarget.style.color = '#94a3b8'; }}>
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className={errors.secret_key ? "text-red-500" : ""}>{t("secretKey")}</Label>
                <div className="flex gap-2">
                  <Input value={formData.secret_key} readOnly className={`font-mono text-sm ${errors.secret_key ? "error-border" : ""}`} type="password" placeholder={t("apisettingssecretKeyPlaceholder")} />
                  <button onClick={() => copyToClipboard(formData.secret_key)} className="px-3 rounded-md border transition-all"
                    style={{ background: 'transparent', border: '1px solid #1e3a5f', color: '#94a3b8' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#06b6d4'; e.currentTarget.style.color = '#67e8f9'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e3a5f'; e.currentTarget.style.color = '#94a3b8'; }}>
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("callbackUrl")}</Label>
                <Input value={formData.callback_url} onChange={(e) => handleChange("callback_url", e.target.value)} placeholder={t("callbackUrlPlaceholder")} />
              </div>

              <div className="space-y-2">
                <Label>{t("webhookUrl")}</Label>
                <Input value={formData.webhook_url} onChange={(e) => handleChange("webhook_url", e.target.value)} placeholder={t("webhookUrlPlaceholder")} />
              </div>

              <div className="space-y-2">
                <Label>{t("ipWhitelist")}</Label>
                <div className="flex gap-2">
                  <Input value={newIp} onChange={(e) => setNewIp(e.target.value)} placeholder={t("ipWhitelistPlaceholder")} onKeyDown={(e) => e.key === "Enter" && addIp()} />
                  <Button onClick={addIp} style={btnPrimary} className="transition-all hover:shadow-[0_0_12px_rgba(6,182,212,0.5)] hover:-translate-y-0.5">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.ip_whitelist?.map((ip) => (
                    <Badge key={ip} className="mf-ip-badge gap-1 px-2 py-1">
                      {ip}
                      <button onClick={() => removeIp(ip)} className="ml-1 transition-colors hover:text-red-400"><X className="h-3 w-3" /></button>
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("rateLimit")}</Label>
                <Input type="number" value={formData.rate_limit} onChange={(e) => handleChange("rate_limit", parseInt(e.target.value))} />
              </div>
            </TabsContent>

            {/* LIMITS */}
            <TabsContent value="limits" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>{t("minDepositThb")}</Label>
                  <Input type="number" value={formData.min_deposit} onChange={(e) => handleChange("min_deposit", parseInt(e.target.value))} /></div>
                <div className="space-y-2"><Label>{t("maxDepositThb")}</Label>
                  <Input type="number" value={formData.max_deposit} onChange={(e) => handleChange("max_deposit", parseInt(e.target.value))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>{t("minWithdrawalThb")}</Label>
                  <Input type="number" value={formData.min_withdrawal} onChange={(e) => handleChange("min_withdrawal", parseInt(e.target.value))} /></div>
                <div className="space-y-2"><Label>{t("maxWithdrawalThb")}</Label>
                  <Input type="number" value={formData.max_withdrawal} onChange={(e) => handleChange("max_withdrawal", parseInt(e.target.value))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>{t("dailyDepositLimitThb")}</Label>
                  <Input type="number" value={formData.daily_deposit_limit} onChange={(e) => handleChange("daily_deposit_limit", parseInt(e.target.value))} /></div>
                <div className="space-y-2"><Label>{t("dailyWithdrawalLimitThb")}</Label>
                  <Input type="number" value={formData.daily_withdrawal_limit} onChange={(e) => handleChange("daily_withdrawal_limit", parseInt(e.target.value))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>{t("depositFeeThb")}</Label>
                  <Input type="number" value={formData.deposit_fee} onChange={(e) => handleChange("deposit_fee", parseFloat(e.target.value))} step="0.01" /></div>
                <div className="space-y-2"><Label>{t("withdrawalFeeThb")}</Label>
                  <Input type="number" value={formData.withdrawal_fee} onChange={(e) => handleChange("withdrawal_fee", parseFloat(e.target.value))} step="0.01" /></div>
              </div>
              <div className="space-y-2">
                <Label>{t("settlementPeriod")}</Label>
                <Select value={formData.settlement_period} onValueChange={(v) => handleChange("settlement_period", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="T0">{t("t0SameDay")}</SelectItem>
                    <SelectItem value="T1">{t("t1NextDay")}</SelectItem>
                    <SelectItem value="T3">{t("t3")}</SelectItem>
                    <SelectItem value="T7">{t("t7Weekly")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            {/* CONTACT */}
            <TabsContent value="contact" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{t("contactName")}</Label>
                <Input value={formData.contact_name} onChange={(e) => handleChange("contact_name", e.target.value)} placeholder={t("contactNamePlaceholder")} />
              </div>
              <div className="space-y-2">
                <Label>{t("contactEmail")}</Label>
                <Input type="email" value={formData.contact_email} onChange={(e) => handleChange("contact_email", e.target.value)} placeholder={t("contactEmailPlaceholder")} />
              </div>
              <div className="space-y-2">
                <Label>{t("contactPhone")}</Label>
                <Input value={formData.contact_phone} onChange={(e) => handleChange("contact_phone", e.target.value)} placeholder={t("contactPhonePlaceholder")} />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6 flex justify-between">
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} style={btnOutline} className="transition-all"
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#06b6d4'; e.currentTarget.style.color = '#67e8f9'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e3a5f'; e.currentTarget.style.color = '#94a3b8'; }}>
                {t("cancel")}
              </Button>
              {!isFirstTab && (
                <Button variant="outline" onClick={() => setCurrentTab(tabOrder[currentTabIndex - 1])} style={btnOutline} className="transition-all"
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#06b6d4'; e.currentTarget.style.color = '#67e8f9'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e3a5f'; e.currentTarget.style.color = '#94a3b8'; }}>
                  {t("backButton")}
                </Button>
              )}
            </div>
            <div>
              {!isLastTab ? (
                <Button onClick={tabChangeHandler} style={btnPrimary} className="transition-all hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] hover:-translate-y-0.5">
                  {t("nextButton")}
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={saving} style={btnPrimary} className="transition-all hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] hover:-translate-y-0.5 disabled:opacity-50">
                  {saving ? t("saving") : merchant ? t("updateMerchant") : t("createMerchantBtn")}
                </Button>
              )}
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
      <Toaster />
    </Dialog>
  );
}